import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ShoppingCart, User, Globe, Menu } from 'lucide-react'
import { useAuthStore, useCartStore, useLanguageStore } from '../../lib/store'
import { SearchBar } from '../ui/SearchBar'

interface HeaderProps {
    sidebarOpen?: boolean
    onToggleSidebar?: () => void
}

export default function Header({ sidebarOpen, onToggleSidebar }: HeaderProps) {
    const { t, i18n } = useTranslation()
    const { user, isAuthenticated, logout } = useAuthStore()
    const { items, toggleCart } = useCartStore()
    const { language, setLanguage } = useLanguageStore()

    const handleLanguageChange = () => {
        const newLang = language === 'en' ? 'vi' : 'en'
        setLanguage(newLang)
        i18n.changeLanguage(newLang)
    }

    return (
        <header className={`fixed top-0 right-0 bg-white shadow-sm z-40 transition-all duration-300 ${sidebarOpen ? 'lg:left-64' : 'lg:left-16'} left-0`}>
            <div className="px-4 lg:px-6">
                <div className="flex items-center justify-between h-16">
                    {/* Left: Sidebar Toggle + Logo */}
                    <div className="flex items-center gap-3">
                        {/* Mobile Sidebar Toggle */}
                        <button
                            onClick={onToggleSidebar}
                            className="lg:hidden p-2 hover:bg-gray-100 rounded-lg transition"
                        >
                            <Menu className="w-5 h-5 text-gray-600" />
                        </button>

                        {/* Desktop Sidebar Toggle */}
                        <button
                            onClick={onToggleSidebar}
                            className="hidden lg:flex items-center justify-center p-2 hover:bg-gray-100 rounded-lg transition"
                        >
                            <Menu className="w-5 h-5 text-gray-600" />
                        </button>

                        {/* Mobile Logo - no ml-10 as hamburger is now inside header */}
                        <Link to="/" className="flex items-center gap-2 lg:hidden">
                            <div className="w-8 h-8 bg-primary-600 rounded-lg flex items-center justify-center text-white font-bold">
                                PQ
                            </div>
                            <span className="font-bold">Phú Quốc</span>
                        </Link>
                    </div>

                    {/* Center: Search with Auto-suggest */}
                    <div className="hidden md:flex flex-1 max-w-md mx-4">
                        <SearchBar
                            className="w-full"
                            inputClassName="bg-gray-100 border-0 focus:bg-white focus:border-gray-200"
                        />
                    </div>

                    {/* Right: Actions */}
                    <div className="flex items-center gap-2 lg:gap-4">
                        {/* Language Switcher */}
                        <button
                            onClick={handleLanguageChange}
                            className="flex items-center gap-1 px-2 py-1.5 text-gray-700 hover:bg-gray-100 rounded-lg transition"
                        >
                            <Globe className="w-4 h-4" />
                            <span className="text-sm font-medium hidden sm:inline">{language.toUpperCase()}</span>
                        </button>

                        {/* Cart */}
                        <button
                            onClick={toggleCart}
                            className="relative p-2 text-gray-700 hover:bg-gray-100 rounded-lg transition"
                        >
                            <ShoppingCart className="w-5 h-5" />
                            {items.length > 0 && (
                                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                                    {items.length}
                                </span>
                            )}
                        </button>

                        {/* User Menu */}
                        {isAuthenticated ? (
                            <div className="relative group">
                                <button className="flex items-center gap-2 p-2 text-gray-700 hover:bg-gray-100 rounded-lg transition">
                                    <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                                        <User className="w-4 h-4" />
                                    </div>
                                    <span className="hidden lg:block text-sm font-medium max-w-[100px] truncate">
                                        {user?.fullName}
                                    </span>
                                </button>
                                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg py-2 hidden group-hover:block border">
                                    <Link
                                        to={user?.role === 'admin' ? '/admin' : user?.role === 'owner' ? '/owner' : '/dashboard'}
                                        className="block px-4 py-2 text-gray-700 hover:bg-gray-100"
                                    >
                                        {t('dashboard')}
                                    </Link>
                                    <Link to="/dashboard/profile" className="block px-4 py-2 text-gray-700 hover:bg-gray-100">
                                        {t('profile')}
                                    </Link>
                                    <hr className="my-1" />
                                    <button
                                        onClick={logout}
                                        className="block w-full text-left px-4 py-2 text-red-600 hover:bg-gray-100"
                                    >
                                        {t('logout')}
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <Link to="/login" className="hidden lg:flex btn btn-primary text-sm">
                                {t('login')}
                            </Link>
                        )}
                    </div>
                </div>
            </div>
        </header>
    )
}
